import { lazy } from "react";
import { Route, Navigate, useParams } from "react-router-dom";
import { lazyWithRetry } from "@/lib/lazyWithRetry";

const ShopifyProductDetailPage = lazyWithRetry(() => import("@/pages/ShopifyProductDetailPage"));
const ShopifyProductsPage = lazyWithRetry(() => import("@/pages/ShopifyProductsPage"));

/**
 * Sanitize legacy product handles before resolving:
 *  - Strip ® (U+00AE) and ™ (U+2122) characters that historically existed
 *    in Shopify handles (later normalized in DB to clean ASCII).
 *    Old indexed URLs: /produkt/biothane%C2%AE-city-koppel-13-mm  → /produkt/biothane-city-koppel-13-mm
 *  - Strip duplicate dashes left behind after removal.
 *  - Lowercase (Shopify handles are always lowercase).
 */
function sanitizeHandle(raw: string | undefined): string {
  if (!raw) return "";
  return decodeURIComponent(raw)
    .toLowerCase()
    .replace(/[®™©]/g, "")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Redirect /product(s)/:handle → /produkt/:handle (with handle sanitization) */
const ProductRedirect = () => {
  const { handle } = useParams();
  return <Navigate to={`/produkt/${sanitizeHandle(handle)}`} replace />;
};

/**
 * Wrapper that detects legacy ®/™ in the URL and 301-style redirects to the cleaned handle.
 * If the handle is already clean, renders the product page directly.
 */
const ProductHandleResolver = () => {
  const { handle } = useParams();
  if (!handle) return <ShopifyProductDetailPage />;
  const decoded = decodeURIComponent(handle);
  const clean = sanitizeHandle(handle);
  if (decoded !== clean && clean.length > 0) {
    return <Navigate to={`/produkt/${clean}`} replace />;
  }
  return <ShopifyProductDetailPage />;
};

export const productRoutes = (
  <>
    <Route path="/produkt/:handle" element={<ProductHandleResolver />} />
    <Route path="/product/:handle" element={<ProductRedirect />} />
    <Route path="/products/:handle" element={<ProductRedirect />} />
    <Route path="/produkter" element={<ShopifyProductsPage />} />
    <Route path="/ryttare" element={<Navigate to="/hast/ridning-utrustning" replace />} />
  </>
);
